import { apiClient, type ManualCommandConfig } from '@/services/http/client'
import { id, object, digest, type Data } from './workflow'

const root = '/legal-source-reviews'
const manual = { manualCommand: true } as ManualCommandConfig
const exact = (v: Data, names: string[]) => {
  if (Object.keys(v).sort().join('|') !== [...names].sort().join('|'))
    throw new Error('Exact original review unavailable')
}
const stamp = (v: unknown) => {
  if (typeof v !== 'string' || !Number.isFinite(Date.parse(v)))
    throw new Error('Invalid receipt time')
}
const hash = (v: unknown) => {
  if (typeof v !== 'string' || !digest.test(v)) throw new Error('Invalid evidence fingerprint')
}
export function evidence(v: unknown): string {
  if (
    typeof v !== 'string' ||
    v.length < 8 ||
    v.length > 256 ||
    v.trim() !== v ||
    /[\x00-\x1f\x7f]/.test(v)
  )
    throw new Error('Use a bounded evidence reference')
  return v
}
export type Action = 'approve' | 'reject' | 'withdraw'
export interface Command {
  review_id: string
  evidence_sha256: string
  acknowledge_no_authority: true
  decision?: 'approve' | 'reject'
  evidence_reference?: string
}
interface Actor {
  user_id: string
  session_id: string
  authorization_reference: string
}
export interface Receipt extends Actor {
  command: Omit<Command, 'acknowledge_no_authority'> & {
    acknowledge_review_only: true
    anchor_request_id: string
    history_sha256: string
    target: Data
    expires_at: string
    evidence_reference: string
  }
  submitted_at: string
  authority_granted: false
  decision: (Actor & { command: Command; decided_at: string }) | null
  revocation: (Actor & { command: Command; revoked_at: string }) | null
}
export function receipt(value: unknown, reference: string): Receipt {
  const canonicalId = (value: unknown) => {
    const normalized = id(value)
    if (normalized !== value) throw new Error('Canonical receipt reference required')
    return normalized
  }
  const r = object(value),
    c = object(r.command),
    t = object(c.target)
  exact(r, [
    'command',
    'user_id',
    'session_id',
    'authorization_reference',
    'submitted_at',
    'decision',
    'revocation',
    'authority_granted',
  ])
  exact(c, [
    'review_id',
    'anchor_request_id',
    'history_sha256',
    'target',
    'evidence_reference',
    'evidence_sha256',
    'expires_at',
    'acknowledge_review_only',
  ])
  exact(t, [
    'id',
    'tenant_id',
    'customer_id',
    'subject_type',
    'period_basis',
    'period_value',
    'current_revision',
    'archived',
  ])
  if (
    r.authority_granted !== false ||
    canonicalId(c.review_id) !== canonicalId(reference) ||
    c.acknowledge_review_only !== true ||
    t.subject_type !== 'HUF' ||
    t.archived !== false ||
    !Number.isInteger(t.current_revision) ||
    Number(t.current_revision) < 1 ||
    Number(t.current_revision) > 2147483647
  )
    throw new Error('Exact review-only source receipt required')
  canonicalId(c.anchor_request_id)
  canonicalId(t.id)
  canonicalId(t.tenant_id)
  hash(c.history_sha256)
  hash(c.evidence_sha256)
  evidence(c.evidence_reference)
  stamp(c.expires_at)
  stamp(r.submitted_at)
  for (const [key, max] of [
    ['customer_id', 100],
    ['period_basis', 100],
    ['period_value', 20],
  ] as const)
    if (typeof t[key] !== 'string' || !(t[key] as string).length || (t[key] as string).length > max)
      throw new Error('Exact source labels unavailable')
  for (const actor of [r, r.decision, r.revocation]) {
    if (actor === null) continue
    const a = object(actor)
    for (const key of ['user_id', 'session_id', 'authorization_reference']) canonicalId(a[key])
  }
  for (const [key, time] of [
    ['decision', 'decided_at'],
    ['revocation', 'revoked_at'],
  ] as const) {
    if (r[key] === null) continue
    const a = object(r[key]),
      cmd = object(a.command)
    exact(a, ['command', 'user_id', 'session_id', 'authorization_reference', time])
    exact(
      cmd,
      key === 'decision'
        ? ['review_id', 'decision', 'evidence_sha256', 'acknowledge_no_authority']
        : ['review_id', 'evidence_reference', 'evidence_sha256', 'acknowledge_no_authority']
    )
    if (
      canonicalId(cmd.review_id) !== canonicalId(reference) ||
      cmd.acknowledge_no_authority !== true ||
      (key === 'decision' && !['approve', 'reject'].includes(String(cmd.decision)))
    )
      throw new Error('Original recorded command differs')
    if (key === 'revocation') evidence(cmd.evidence_reference)
    hash(cmd.evidence_sha256)
    stamp(a[time])
  }
  return r as unknown as Receipt
}
export async function inspect(reference: string): Promise<Receipt> {
  const ref = id(reference)
  return receipt((await apiClient.get(`${root}/${ref}`, manual)).data, ref)
}
export function command(
  reference: string,
  action: Action,
  fingerprint: string,
  reason: string
): Command {
  hash(fingerprint)
  return {
    review_id: id(reference),
    evidence_sha256: fingerprint,
    acknowledge_no_authority: true,
    ...(action === 'withdraw' ? { evidence_reference: evidence(reason) } : { decision: action }),
  }
}
export async function send(cmd: Command, action: Action): Promise<void> {
  await apiClient.post(
    `${root}/${id(cmd.review_id)}/${action === 'withdraw' ? 'revocations' : 'decisions'}`,
    cmd,
    manual
  )
}
export async function fingerprint(cmd: Command): Promise<string> {
  const ordered = Object.fromEntries(Object.entries(cmd).sort(([a], [b]) => a.localeCompare(b)))
  const result = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(JSON.stringify(ordered))
  )
  return Array.from(new Uint8Array(result), b => b.toString(16).padStart(2, '0')).join('')
}
export interface Intent {
  review: string
  action: Action
  fingerprint: string
  user: string
  session: string
}
const storageKey = (account: string) => 'taxhive:source-review-intent:v1:' + id(account)
export function pending(account: string): Intent | null {
  const raw = localStorage.getItem(storageKey(account))
  if (!raw) return null
  if (raw.length > 1024) throw new Error('Saved original intent unavailable')
  const p = object(JSON.parse(raw))
  exact(p, ['review', 'action', 'fingerprint', 'user', 'session'])
  id(p.review)
  id(p.user)
  id(p.session)
  hash(p.fingerprint)
  if (!['approve', 'reject', 'withdraw'].includes(String(p.action)) || id(p.user) !== id(account))
    throw new Error('Saved original intent differs')
  return p as unknown as Intent
}
export function remember(intent: Intent) {
  if (pending(intent.user)) throw new Error('Recover the saved original outcome first')
  const raw = JSON.stringify(intent),
    key = storageKey(intent.user)
  localStorage.setItem(key, raw)
  if (localStorage.getItem(key) !== raw) throw new Error('Original intent could not be saved')
}
export function forget(account: string) {
  localStorage.removeItem(storageKey(account))
}

// These API statuses are explicit pre-write refusals. Unknown/5xx outcomes stay pending.
export function prewriteRefusal(error: unknown): boolean {
  const response = error && typeof error === 'object' && 'response' in error ? error.response : null
  return (
    !!response &&
    typeof response === 'object' &&
    'status' in response &&
    [401, 403, 404, 413, 415, 422].includes(Number(response.status))
  )
}
